import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  SmartToy as BotIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  Code as CodeIcon,
  OpenInNew as OpenInNewIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { Message } from '../../types/chat';
import ReactMarkdown from 'react-markdown';
import { triggerNavigation } from '../../hooks/useNavigationHandler';
import ProspoLogo from '../../assets/images/icons/Prospo.svg';
import { AI_NOTE_TOOLS } from '../../hooks/useNoteChangeListener';
import { AI_FILE_UPLOAD_REQUESTED_EVENT } from '../../hooks/useFileChangeListener';

interface ChatMessageProps {
  message: Message;
}

const formatToolName = (name: string) => {
  if (!name) return 'System Action';
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getOperationError = (operation: any): string | null => {
  if (operation?.error) return String(operation.error);
  if (operation?.result?.success === false || operation?.result?.error) {
    return String(operation.result.message || operation.result.error || 'Action failed');
  }
  return null;
};

const FormattedText: React.FC<{ content: string }> = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldCollapse = content.length > 800;
  const displayContent = shouldCollapse && !isExpanded
    ? content.substring(0, 400) + '...'
    : content;

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{
        '& p': { m: 0, mb: 1, lineHeight: 1.5 },
        '& ul, & ol': { ml: 2, mb: 1 },
        '& li': { mb: 0.5 },
        '& code': {
          bgcolor: 'rgba(0,0,0,0.06)',
          px: 0.6, py: 0.2,
          borderRadius: 1,
          fontFamily: 'Consolas, Monaco, monospace',
          fontSize: '0.85em',
          color: '#d32f2f'
        },
        '& pre': {
          bgcolor: '#2d2d2d',
          color: '#fff',
          p: 1.5,
          borderRadius: 2,
          maxWidth: '100%',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          overflowX: 'hidden',
          fontSize: '0.85em',
          '& code': {
            color: 'inherit', bgcolor: 'transparent', p: 0,
            whiteSpace: 'inherit', overflowWrap: 'inherit', wordBreak: 'inherit'
          }
        }
      }}>
        <ReactMarkdown
          components={{
            a: ({ node, ...props }) => {
              void node;
              return <a {...props} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontWeight: 500, textDecoration: 'underline' }} />;
            }
          }}
        >{displayContent}</ReactMarkdown>
      </Box>

      {shouldCollapse && (
        <Button
          size="small"
          variant="contained"
          onClick={() => setIsExpanded(!isExpanded)}
          startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            mt: 1,
            fontSize: '0.75rem',
            textTransform: 'none',
            borderRadius: '12px',
            px: 1.5,
            py: 0.5,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Box>
  );
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming;
  const emailNavigationOperation = message.toolOperations?.find((operation: any) =>
    operation?.tool === 'navigate_app'
    && operation?.result?.success
    && typeof operation?.result?.route === 'string'
    && /^\/client\/[^/]+\/email(?:\?|$)/.test(operation.result.route),
  );
  const emailNavigationTarget = emailNavigationOperation
    ? { route: emailNavigationOperation.result.route, state: emailNavigationOperation.result.state }
    : message.emailLink;
  const privateDocumentOperation = message.toolOperations?.find((operation: any) =>
    (operation?.tool === 'create_private_document' || operation?.toolName === 'create_private_document')
    && operation?.result?.success !== false
    && typeof (operation?.result?.route || operation?.result?.data?.route) === 'string',
  );
  const privateDocumentRoute = privateDocumentOperation
    ? privateDocumentOperation.result.route || privateDocumentOperation.result.data.route
    : null;

  // Logic: Combine content + guidance if available
  let displayContent = message.content || '';

  // Check for navigation guidance if content is empty
  if (!displayContent && message.toolOperations) {
    const navOp = message.toolOperations.find((op: any) =>
      (op.tool === 'navigate_app' || op.toolName === 'navigate_app') &&
      op.result && op.result.success
    );
    if (navOp && navOp.result.guidance) {
      displayContent = `_${navOp.result.guidance}_`;
    }
  }

  // Note tools are excluded from the debug accordion entirely — they're
  // conversational (the model's own reply already covers what happened), and
  // their error text is written for the model to paraphrase, not for verbatim
  // display (it may reference tool/parameter names).
  const visibleToolOperations = message.toolOperations?.filter(
    (op: any) => !AI_NOTE_TOOLS.has(op.tool),
  );

  // Check if any operation failed (to auto-expand accordion)
  const hasError = visibleToolOperations?.some((op: any) => Boolean(getOperationError(op)));

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
        px: 0.5
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          gap: 1.5,
          maxWidth: '92%'
        }}
      >
        <Avatar
          src={isUser ? undefined : ProspoLogo}
          imgProps={{ style: { objectFit: 'cover', objectPosition: 'left center' } }}
          sx={{
            width: 28, height: 28,
            bgcolor: isUser ? 'primary.main' : 'white',
            border: isUser ? 'none' : '1px solid #e0e0e0',
            color: isUser ? 'white' : 'primary.main',
            boxShadow: isUser ? 2 : 0
          }}
        >
          {isUser ? <PersonIcon sx={{ fontSize: 20 }} /> : <BotIcon sx={{ fontSize: 20 }} />}
        </Avatar>

        <Box sx={{
          maxWidth: isUser ? '280px' : '400px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start'
        }}>
          {displayContent && (
            <Paper
              elevation={isUser ? 4 : 1}
              sx={{
                p: '12px 16px',
                bgcolor: isUser ? 'primary.main' : 'background.paper',
                color: isUser ? 'primary.contrastText' : 'text.primary',
                borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                border: isUser ? 'none' : '1px solid #f0f0f0',
                position: 'relative',
                fontSize: '0.9rem',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
                overflowWrap: 'anywhere'
              }}
            >
      <FormattedText content={displayContent} />
      {message.attachments && message.attachments.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {message.attachments.map((attachment) => (
            <Chip
              key={attachment.id}
              size="small"
              icon={<AttachFileIcon />}
              label={attachment.fileName}
              variant="outlined"
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Box>
      )}

              {isStreaming && (
                <span style={{
                  display: 'inline-block', width: 6, height: 14,
                  backgroundColor: 'currentColor', marginLeft: 4,
                  verticalAlign: 'middle', opacity: 0.7
                }} className="cursor-blink" />
              )}
            </Paper>
          )}

          <Typography
            variant="caption"
            sx={{
              mt: 0.5, opacity: 0.6, fontSize: '0.7rem', px: 1,
              alignSelf: isUser ? 'flex-end' : 'flex-start'
            }}
          >
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>

          {/* Meeting link — shown after a meeting is created */}
          {message.meetingLink && (
            <Chip
              icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
              label="View Meeting"
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => triggerNavigation({
                actionType: 'navigate',
                route: `/meetings/${message.meetingLink!.meetingId}`,
              })}
              sx={{ mt: 1, cursor: 'pointer', fontWeight: 500 }}
            />
          )}

          {/* Workflow link — shown after build_workflow creates a workflow */}
          {message.workflowLink && (
            <Chip
              icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
              label="View Workflow"
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => triggerNavigation({
                actionType: 'navigate',
                route: `/admin?tab=workflows&workflowId=${message.workflowLink!.workflowId}`,
              })}
              sx={{ mt: 1, cursor: 'pointer', fontWeight: 500 }}
            />
          )}

          {emailNavigationTarget && (
            <Chip
              icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
              label="View Email"
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => triggerNavigation({
                actionType: 'navigate',
                route: emailNavigationTarget.route,
                state: emailNavigationTarget.state,
              })}
              sx={{ mt: 1, cursor: 'pointer', fontWeight: 500 }}
            />
          )}

          {privateDocumentRoute && (
            <Chip
              icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
              label="Open file"
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => triggerNavigation({
                actionType: 'navigate',
                route: privateDocumentRoute,
              })}
              sx={{ mt: 1, cursor: 'pointer', fontWeight: 500 }}
            />
          )}

          {/* Upload trigger — the agent can only OFFER to open the native file
              picker; browsers require the actual .click() on the hidden file
              input to happen inside a genuine user gesture, so THIS button's
              click (not the async chat response) is what makes it work. */}
          {message.needsFileUploadTrigger && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon sx={{ fontSize: '16px !important' }} />}
              onClick={() => window.dispatchEvent(new CustomEvent(AI_FILE_UPLOAD_REQUESTED_EVENT))}
              sx={{ mt: 1, fontWeight: 500 }}
            >
              Choose file(s) to upload
            </Button>
          )}

          {/* Tool operations accordion */}
          {visibleToolOperations && visibleToolOperations.length > 0 && (
            <Box sx={{ mt: 1, width: '100%' }}>
              <Accordion
                defaultExpanded={hasError} // Auto expand on error
                elevation={0}
                sx={{
                  bgcolor: 'transparent',
                  border: '1px solid',
                  borderColor: hasError ? 'error.light' : 'divider',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize="small" />}
                  sx={{
                    minHeight: 32,
                    px: 1.5,
                    '& .MuiAccordionSummary-content': { my: 0.5 }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CodeIcon fontSize="small" color="action" />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      Executed {visibleToolOperations.length} Action{visibleToolOperations.length > 1 ? 's' : ''}
                    </Typography>
                    {hasError && <Chip label="Error" size="small" color="error" sx={{ height: 16, fontSize: '0.6rem' }} />}
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {visibleToolOperations.map((operation: any, index) => {
                      const operationError = getOperationError(operation);
                      const isError = Boolean(operationError);
                      const toolName = formatToolName(operation.tool || operation.toolName);

                      return (
                        <Box
                          key={index}
                          sx={{
                            p: 1,
                            bgcolor: isError ? '#fff8f8' : '#f8f9fa',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: isError ? 'error.light' : 'divider'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" fontWeight={600} color="text.primary">
                              {toolName}
                            </Typography>
                            {isError ? <ErrorIcon color="error" fontSize="small" sx={{ width: 16 }} /> : <CheckIcon color="success" fontSize="small" sx={{ width: 16 }} />}
                          </Box>

                          {operationError && (
                            <Typography variant="caption" color="error.main" sx={{ display: 'block', fontFamily: 'monospace' }}>
                              {operationError}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Box>
      </Box>
      <style>{`
        .cursor-blink { animation: blink 1s infinite; }
        @keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
      `}</style>
    </Box>
  );
};
